import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { Plus, Search, Zap, AlertTriangle } from 'lucide-react'
import { useTenants } from '@/hooks/useTenants'
import { useHealthChecks } from '@/hooks/useHealthChecks'
import { Button, EmptyState } from '@/components/ui'
import { TenantGrid } from '@/components/tenants/TenantGrid'
import type { TTenant } from '@/types'

// ── Filter types ───────────────────────────────────────────────────────────

type FilterKey = 'all' | 'active' | 'provisioning' | 'dormant' | 'unhealthy' | 'suspended'

const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'provisioning', label: 'Provisioning' },
  { key: 'dormant', label: 'Dormant' },
  { key: 'unhealthy', label: 'Unhealthy' },
  { key: 'suspended', label: 'Suspended' },
]

// ── Tenants Page ───────────────────────────────────────────────────────────

export default function TenantsPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all')
  const { data: tenants = [], isLoading, isError } = useTenants()
  const { data: healthMap = new Map() } = useHealthChecks()

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const filteredTenants = useMemo(() => {
    let result = tenants

    if (activeFilter !== 'all') {
      if (activeFilter === 'unhealthy') {
        result = result.filter(t => {
          const hc = healthMap.get(t.id)
          return hc ? !hc.is_healthy : t.status === 'provision_failed'
        })
      } else {
        result = result.filter(t => t.status === activeFilter)
      }
    }

    if (debouncedQuery) {
      const q = debouncedQuery.toLowerCase()
      result = result.filter(t =>
        t.name?.toLowerCase().includes(q) ||
        t.slug?.toLowerCase().includes(q) ||
        t.subdomain?.toLowerCase().includes(q)
      )
    }

    return result
  }, [tenants, activeFilter, debouncedQuery, healthMap])

  const counts = useMemo(() => {
    const active = tenants.filter(t => t.status === 'active').length
    const provisioning = tenants.filter(t => t.status === 'provisioning').length
    return { active, provisioning, total: tenants.length }
  }, [tenants])

  const handleTenantClick = useCallback((tenant: TTenant) => {
    navigate(`/tenants/${tenant.id}`)
  }, [navigate])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}>
          <div>
            <h1 style={{
              fontSize: '20px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              margin: 0,
              lineHeight: 1.3,
            }}>
              Tenants
            </h1>
            {!isLoading && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginTop: '6px',
                fontSize: '13px',
                color: 'var(--text-secondary)',
              }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--status-healthy)', display: 'inline-block' }} />
                  {counts.active} active
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--status-warning)', display: 'inline-block' }} />
                  {counts.provisioning} provisioning
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--text-tertiary)', display: 'inline-block' }} />
                  {counts.total} total
                </span>
              </div>
            )}
          </div>

          <Button
            variant="primary"
            size="md"
            icon={<Plus size={16} />}
            onClick={() => navigate('/tenants/new')}
          >
            New Tenant
          </Button>
        </div>

        {/* Search + Filter Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: '320px' }}>
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)',
                pointerEvents: 'none',
              }}
            />
            <input
              type="text"
              placeholder="Search tenants..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="input-field"
              style={{
                width: '100%',
                padding: '8px 14px 8px 34px',
                backgroundColor: 'var(--bg-tertiary)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                fontSize: '13px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-sans)',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {FILTER_OPTIONS.map(filter => (
              <button
                key={filter.key}
                className={`filter-chip${activeFilter === filter.key ? ' active' : ''}`}
                onClick={() => setActiveFilter(filter.key)}
                style={{
                  padding: '6px 14px',
                  fontSize: '12px',
                  fontWeight: 500,
                  borderRadius: 'var(--radius-full)',
                  border: '1px solid var(--border-subtle)',
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  whiteSpace: 'nowrap',
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Error banner */}
        {isError && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 16px',
            marginBottom: 16,
            backgroundColor: 'var(--status-unhealthy-dim)',
            border: '1px solid var(--status-unhealthy)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13,
            color: 'var(--status-unhealthy)',
          }}>
            <AlertTriangle size={16} />
            Failed to load tenants -- check PB auth and collection rules.
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <TenantGrid tenants={[]} healthMap={new Map()} onTenantClick={() => {}} isLoading />
        ) : filteredTenants.length === 0 && tenants.length === 0 ? (
          <EmptyState
            icon={<Zap size={48} />}
            title="No tenants yet"
            description="Provision your first tenant to get started"
            action={{
              label: '+ New Tenant',
              onClick: () => navigate('/tenants/new'),
              icon: <Plus size={16} />,
            }}
          />
        ) : filteredTenants.length === 0 ? (
          <EmptyState
            icon={<Search size={48} />}
            title="No matching tenants"
            description="Try adjusting your search or filter criteria"
          />
        ) : (
          <TenantGrid
            tenants={filteredTenants}
            healthMap={healthMap}
            onTenantClick={handleTenantClick}
          />
        )}
      </div>

    </div>
  )
}
